import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Skeleton,
    Alert,
    Pagination,
    Chip,
    Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchResultCard from './SearchResultCard';
import SearchFilters from './SearchFilters';
import {
    searchService,
    SearchResult,
    SearchFacets,
    SearchResultType,
} from '../../services/searchService';
import { useTranslation } from '../../i18n';

const RESULTS_PER_PAGE = 10;

const SearchResults: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get('q') || '';
    const [query, setQuery] = useState(initialQuery);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [facets, setFacets] = useState<SearchFacets>({
        types: {},
        difficulty: { beginner: 0, intermediate: 0, advanced: 0 },
    });
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [selectedTypes, setSelectedTypes] = useState<SearchResultType[]>([
        'course',
        'lesson',
        'wiki',
        'learning_path',
        'learning_group',
    ]);
    const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);

    const performSearch = useCallback(
        async (searchQuery: string, pageNum: number, types: SearchResultType[], difficulty: string | null) => {
            if (searchQuery.length < 2) {
                setResults([]);
                setTotal(0);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await searchService.search(searchQuery, {
                    types: types,
                    difficulty: difficulty as any,
                    limit: RESULTS_PER_PAGE,
                    offset: (pageNum - 1) * RESULTS_PER_PAGE,
                });

                setResults(response.results);
                setFacets(response.facets);
                setTotal(response.total);
            } catch (err) {
                console.error('Search error:', err);
                setError(t('search.errorSearch'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    // Search when URL query or filters change
    useEffect(() => {
        const q = searchParams.get('q');
        if (q) {
            setQuery(q);
            performSearch(q, page, selectedTypes, selectedDifficulty);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, page, selectedTypes, selectedDifficulty]);

    const handleSearch = () => {
        if (query.length < 2) return;
        setPage(1);
        setSearchParams({ q: query });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClear = () => {
        setQuery('');
        setResults([]);
        setTotal(0);
        navigate('/search');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        window.scrollTo(0, 0);
    };

    const handleTypeChange = (types: SearchResultType[]) => {
        setSelectedTypes(types);
        setPage(1);
    };

    const handleDifficultyChange = (difficulty: string | null) => {
        setSelectedDifficulty(difficulty);
        setPage(1);
    };

    const totalPages = Math.ceil(total / RESULTS_PER_PAGE);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                {t('search.title')}
            </Typography>

            {/* Search Input */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('search.placeholder')}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: query ? (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClear}>
                                    <CloseIcon />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            </Box>

            {/* Results Summary */}
            {query && !loading && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('search.resultsFor', { query })}:
                    </Typography>
                    <Chip label={`${total} ${t('search.results')}`} size="small" />
                </Box>
            )}

            <Grid container spacing={3}>
                {/* Filters Sidebar */}
                <Grid size={{ xs: 12, md: 3 }}>
                    <SearchFilters
                        facets={facets}
                        selectedTypes={selectedTypes}
                        selectedDifficulty={selectedDifficulty}
                        onTypeChange={handleTypeChange}
                        onDifficultyChange={handleDifficultyChange}
                    />
                </Grid>

                {/* Results */}
                <Grid size={{ xs: 12, md: 9 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box>
                            {[1, 2, 3].map((i) => (
                                <Skeleton
                                    key={i}
                                    variant="rounded"
                                    height={120}
                                    sx={{ mb: 2 }}
                                />
                            ))}
                        </Box>
                    ) : results.length === 0 ? (
                        query.length >= 2 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="h6" color="text.secondary">
                                    {t('search.noResults')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('search.tryDifferentTerms')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">
                                    {t('search.enterQuery')}
                                </Typography>
                            </Box>
                        )
                    ) : (
                        <>
                            {results.map((result) => (
                                <SearchResultCard key={`${result.type}-${result.id}`} result={result} />
                            ))}

                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default SearchResults;
